import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Spinner from './Spinner';
import {
  consultAI,
  AIConsultRequest,
  AIConsultResponse,
} from '../services/api';
import styles from './FloatingAIAssistant.module.css';

interface FieldContext {
  fieldId?: string;
  fieldLabel?: string;
  currentValue?: string;
  allFormData?: Record<string, unknown>;
}

interface FloatingAIAssistantProps {
  // 表示制御
  isOpen: boolean;
  onToggle: () => void;

  // 動的オフセット(推奨)
  topOffset?: number;  // デフォルト: 自動計算
  containerRef?: React.RefObject<HTMLElement>;  // 測定対象のコンテナ(複数フォーム対応用)
  contentRef?: React.RefObject<HTMLElement>;

  // コンテキスト情報(オプション)
  currentSection?: string;
  currentField?: string;

  // BCP種別(オプション)
  bcpType?: 'simple' | 'basic';

  // RegulationPageNew等の独自ロジック用(オプション)
  onApply?: (content: string) => void;
  onClose?: () => void;
  fieldContext?: FieldContext;
  isSmallScreen?: boolean;
  displayMode?: 'floating' | 'inline';
}

const FloatingAIAssistant: React.FC<FloatingAIAssistantProps> = ({
  isOpen,
  onToggle,
  topOffset,
  containerRef,
  contentRef,
  currentSection,
  currentField,
  bcpType = 'simple',
  onApply: _onApply,
  onClose,
  fieldContext,
  isSmallScreen = false,
  displayMode = 'floating',
}) => {
  const [question, setQuestion] = useState('');
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [calculatedOffset, setCalculatedOffset] = useState(144);
  const [assistantWidth, setAssistantWidth] = useState(603);
  const [fieldNotificationMessage, setFieldNotificationMessage] = useState<string | null>(null);
  const [horizontalLeft, setHorizontalLeft] = useState(16);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<string>('calc(100vh - 200px)');

  // チャットエリアの参照
  const chatAreaRef = useRef<HTMLDivElement>(null);

  // フィールド選択時の通知メッセージを設定
  useEffect(() => {
    if (fieldContext?.fieldId && fieldContext?.fieldLabel) {
      setFieldNotificationMessage(`「${fieldContext.fieldLabel}」について質問`);
      // 前回のQ&Aをクリア
      setSubmittedQuestion('');
      setAnswer('');
    } else {
      setFieldNotificationMessage(null);
    }
  }, [fieldContext?.fieldId, fieldContext?.fieldLabel]);

  // 動的オフセット計算と幅計算
  useEffect(() => {
    if (displayMode === 'inline') {
      return;
    }

    if (topOffset !== undefined) {
      setCalculatedOffset(topOffset);
    }

    // 自動計算: ヘッダー要素の高さとアシスタント幅を測定
    const measure = () => {
      // containerRefが提供されている場合はその中で検索、なければdocument全体
      const root = containerRef?.current || document;
      const stepIndicator = root.querySelector('[data-bcp-step-indicator]');

      let offset = 0;

      // step indicatorが存在する場合、その下端位置を使用
      if (stepIndicator) {
        const stepRect = stepIndicator.getBoundingClientRect();
        offset = stepRect.bottom + 32; // Figmaの仕様に合わせて32px
      } else {
        // step indicatorがない場合はheaderのみで計算
        const header = root.querySelector('[data-bcp-header]');
        if (header) {
          const headerRect = header.getBoundingClientRect();
          offset = headerRect.bottom + 32; // Figmaの仕様に合わせて32px
        }
      }

      if (topOffset === undefined) {
        setCalculatedOffset(offset || 144); // フォールバック: 144px
      }

      const viewportWidth = window.innerWidth;
      const nextWidth = viewportWidth >= 1440 ? 580 : viewportWidth >= 1280 ? 480 : viewportWidth;
      setAssistantWidth(nextWidth);

      // AIアシスタントを画面右端から固定位置に配置 (Figmaの仕様に合わせる)
      const rightMargin = 29; // Figmaの仕様: padding-right: 29px
      const calculatedLeft = viewportWidth - nextWidth - rightMargin;
      setHorizontalLeft(Math.max(29, calculatedLeft));
    };

    measure();
    window.addEventListener('resize', measure);
    const observerTarget = contentRef?.current;
    let resizeObserver: ResizeObserver | null = null;
    if (observerTarget && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => measure());
      resizeObserver.observe(observerTarget);
    }

    return () => {
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
    };
  }, [topOffset, containerRef, contentRef, isSmallScreen, displayMode]);

  // 動的maxHeight計算 (inline mode用)
  useEffect(() => {
    if (displayMode !== 'inline') {
      return;
    }

    const calculateMaxHeight = () => {
      const root = document;
      const header = root.querySelector('[data-bcp-header]');
      const stepIndicator = root.querySelector('[data-bcp-step-indicator]');
      const main = root.querySelector('main');

      let totalOffset = 0;

      // ヘッダーの高さ
      if (header) {
        totalOffset += header.getBoundingClientRect().height;
      }

      // ステップインジケーターの高さ
      if (stepIndicator) {
        totalOffset += stepIndicator.getBoundingClientRect().height;
      }

      // mainのpadding-top
      if (main) {
        const mainStyle = window.getComputedStyle(main);
        totalOffset += parseInt(mainStyle.paddingTop) || 0;
      }

      // sticky topの位置 (BCPFormLayout.tsx line 182)
      totalOffset += 32;

      // バッファ (10px程度)
      totalOffset += 10;

      const maxHeight = `calc(100vh - ${totalOffset}px)`;
      setDynamicMaxHeight(maxHeight);
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);

    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, [displayMode]);

  // 閉じる処理(onCloseとonToggleの両方をサポート)
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onToggle();
    }
  };

  // Get display message for consultation context
  const getConsultationMessage = (): string => {
    return 'ご相談内容を入力してください。';
  };

  const isTemplateText = (text: string): boolean => {
    // テンプレート文言のパターン(50名の例など)
    const templatePatterns = [
      /※以下は従業員数50名/,
      /\(従業員数・事業所立地条件・業種特性に応じて数量は自動調整されます\)/,
      /算出結果:\s*50名/,
    ];
    return templatePatterns.some((pattern) => pattern.test(text));
  };

  const buildFormDataPayload = (): Record<string, string> | undefined => {
    if (!fieldContext?.allFormData) {
      return undefined;
    }

    const entries = Object.entries(fieldContext.allFormData).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (value === null || value === undefined) {
          return acc;
        }

        if (typeof value === 'string') {
          const trimmed = value.trim();
          // テンプレート文言は除外
          if (trimmed && !isTemplateText(trimmed)) {
            acc[key] = trimmed;
          }
          return acc;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
          acc[key] = String(value);
          return acc;
        }

        return acc;
      },
      {}
    );

    return Object.keys(entries).length > 0 ? entries : undefined;
  };

  const handleSubmit = async () => {
    if (!question.trim()) return;

    setSubmittedQuestion(question);
    setIsLoading(true);
    setAnswer('');

    const sessionId = sessionStorage.getItem('upload_session_id') || undefined;
    const formDataPayload = buildFormDataPayload();

    const payload: AIConsultRequest = {
      question: question.trim(),
      session_id: sessionId,
      tenant_id: 'default',
      current_section: currentSection,
      current_field: currentField,
      form_data: formDataPayload,
      bcp_type: bcpType,
    };

    try {
      const data: AIConsultResponse = await consultAI(payload);

      if (data.success) {
        setAnswer(data.answer);
        // 自動適用しない - ユーザーが明示的に「適用」ボタンを押すまで待つ
      } else {
        setAnswer(data.error || 'エラーが発生しました');
      }
    } catch (error: any) {
      console.error('AI consult error:', error);

      // 429エラー(クォータ超過)の場合は、グローバルダイアログに任せる
      if (error?.code === '429') {
        setAnswer('月間AI利用上限に達しました。詳細はダイアログをご確認ください。');
      } else {
        setAnswer('AIへの接続に失敗しました。');
      }
    } finally {
      setIsLoading(false);
      setQuestion('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  // トグルボタン(閉じている時のみ表示)
  if (!isOpen) {
    if (displayMode === 'inline') {
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }}
          className="w-full rounded-xl border border-dashed border-blue-200 bg-white py-4 px-6 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50"
        >
          AIアシスタントを開く
        </button>
      );
    }

    return (
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        className="fixed bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
        style={{
          top: `${calculatedOffset + 16}px`,
          right: '16px',
          width: '56px',
          height: '56px',
        }}
        aria-label="AIアシスタントを開く"
      >
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </button>
    );
  }

  const desktopStyle = {
    top: `${calculatedOffset}px`,
    maxHeight: `calc(100vh - ${calculatedOffset}px - 16px)`,
    width: `${assistantWidth}px`,
    minWidth: '400px',
    borderRadius: '12px',
    left: `${horizontalLeft}px`,
  } as const;

  const mobileStyle = {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100vh',
    borderRadius: 0,
  } as const;

  const inlineStyle = {
    width: '100%',
    height: 'fit-content',
    minHeight: '400px',
    maxHeight: dynamicMaxHeight,
    borderRadius: '12px',
  };

  const isFloatingMode = displayMode === 'floating';

  const panelClassName = [
    'bg-white flex flex-col transition-transform duration-300 ease-in-out',
    isFloatingMode ? 'z-40 fixed' : 'shadow-sm',
  ]
    .filter(Boolean)
    .join(' ');

  const panelStyle = isFloatingMode
    ? {
        ...(isSmallScreen ? mobileStyle : desktopStyle),
        boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
      }
    : {
        ...inlineStyle,
        boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
      };

  const headerBaseClass = ['flex justify-between items-center bg-white'];
  if (isFloatingMode) {
    headerBaseClass.push('bg-white sticky top-0');
  }

  const headerStyle = isFloatingMode
    ? {
        height: '81px',
        padding: '16px 16px 17px 16px',
        borderBottom: '1px solid #E5E7EB',
      }
    : {
        padding: '16px 20px',
        borderBottom: '1px solid #E5E7EB',
      };

  const inputContainerClassName = [
    'bg-white flex items-center gap-2',
    isFloatingMode ? 'sticky bottom-0' : 'border-t border-gray-200',
  ].join(' ');

  return (
    <div className={panelClassName} style={panelStyle}>
      {/* ヘッダー */}
      <div className={headerBaseClass.join(' ')} style={headerStyle}>
        <div className="flex-1">
          <h3 className="font-semibold" style={{ color: '#0D357F' }}>AIアシスタント</h3>
          {fieldContext?.fieldLabel && (
            <p className="text-sm text-gray-600 mt-1">
              「{fieldContext.fieldLabel}」について相談中
            </p>
          )}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          aria-label="閉じる"
        >
          ×
        </button>
      </div>

      {/* チャットエリア */}
      <div
        ref={chatAreaRef}
        className={`p-4 ${styles.chatArea}`}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <div className="space-y-4" style={{ width: '100%', maxWidth: '100%', minHeight: 'min-content' }}>
          {/* AI Message Default - ご相談内容を入力してください or フィールド通知 */}
          <div
            className="rounded-lg"
            style={{
              padding: '12px',
              backgroundColor: '#F3F4F6',
              maxWidth: '100%',
            }}
          >
            <p className="text-sm text-gray-600">
              {getConsultationMessage()}
            </p>
          </div>

          {/* ユーザーメッセージ - 送信後に表示 */}
          {submittedQuestion && (
            <div style={{ marginLeft: '40px', maxWidth: '100%' }}>
              <div
                className="rounded-lg"
                style={{
                  padding: '12px',
                  backgroundColor: '#0D357F',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                }}
              >
                <p className="text-sm text-white">{submittedQuestion}</p>
              </div>
            </div>
          )}

          {/* AI回答 */}
          {answer && !isLoading && (
            <div
              className="rounded-lg text-sm text-gray-800"
              style={{
                padding: '12px',
                backgroundColor: '#F3F4F6',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                width: '100%',
                maxWidth: '100%',
                display: 'block',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ width: '100%', maxWidth: '100%', display: 'block', boxSizing: 'border-box', overflow: 'hidden' }}>
                <ReactMarkdown
                  components={{
                    h1: ({...props}) => <h1 className="text-lg font-bold mt-4 mb-2" style={{ wordBreak: 'break-word', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.5' }} {...props} />,
                    h2: ({...props}) => <h2 className="text-base font-bold mt-3 mb-2" style={{ wordBreak: 'break-word', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.5' }} {...props} />,
                    h3: ({...props}) => <h3 className="text-sm font-bold mt-2 mb-1" style={{ wordBreak: 'break-word', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.5' }} {...props} />,
                    h4: ({...props}) => <h4 className="text-sm font-semibold mt-2 mb-1" style={{ wordBreak: 'break-word', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.5' }} {...props} />,
                    p: ({...props}) => <p className="mb-2" style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.6', margin: '0 0 0.5rem 0' }} {...props} />,
                    ul: ({...props}) => <ul className="list-disc mb-2" style={{ display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', wordBreak: 'break-word', paddingLeft: '1.25rem', lineHeight: '1.6' }} {...props} />,
                    ol: ({...props}) => <ol className="list-decimal mb-2" style={{ display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', wordBreak: 'break-word', paddingLeft: '1.25rem', lineHeight: '1.6' }} {...props} />,
                    li: ({...props}) => <li className="mb-1" style={{ wordBreak: 'break-word', display: 'list-item', maxWidth: '100%', boxSizing: 'border-box', lineHeight: '1.6', marginBottom: '0.25rem' }} {...props} />,
                    strong: ({...props}) => <strong className="font-semibold" style={{ wordBreak: 'break-word' }} {...props} />,
                    hr: ({...props}) => <hr className="my-3 border-gray-300" style={{ display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} {...props} />,
                    code: ({...props}) => <code className="bg-gray-200 px-1 rounded" style={{ wordBreak: 'break-all', maxWidth: '100%', boxSizing: 'border-box', fontSize: '0.875em' }} {...props} />,
                    pre: ({...props}) => <pre className="bg-gray-200 p-2 rounded my-2" style={{ display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} {...props} />,
                  }}
                >
                  {answer}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* ローディング中 */}
          {isLoading && (
            <div
              className="rounded-lg"
              style={{
                padding: '12px',
                backgroundColor: '#F3F4F6',
              }}
            >
              <div className="flex items-center">
                <Spinner size="sm" inline />
                <span className="ml-2 text-sm text-gray-600">回答を生成中...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 入力エリア(下部固定) */}
      <div
        className={inputContainerClassName}
        style={{
          height: '78px',
          padding: '17px 16px 16px 16px',
          borderTop: isFloatingMode ? '1px solid #E5E7EB' : undefined,
        }}
      >
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="AIに質問する..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
          disabled={isLoading}
        />
        <button
          onClick={handleSubmit}
          disabled={isLoading || !question.trim()}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap"
          style={{
            backgroundColor: '#0D357F',
          }}
        >
          送信
        </button>
      </div>
    </div>
  );
};

export default FloatingAIAssistant;
